import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { HERO } from "@/config/clinicConfig";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/f017c6d5-a7bd-463e-8e27-b36db03855e2/images/d9445e07978eb7e74299e958add4259cfaa74544c0ba6525d06f941bd6549f1e.jpeg";

const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -right-40 top-20 h-[480px] w-[480px] rounded-full bg-blush/60 blur-3xl" />
      <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 pb-10 pt-8 sm:gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:px-8 lg:pb-12 lg:pt-10">
        {/* Type */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-clay/25 bg-card/60 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-clay"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-clay opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-clay" />
            </span>
            {HERO.eyebrow}
          </motion.p>

          <h1
            className="whitespace-nowrap font-display text-[1.1rem] font-medium leading-[1.15] tracking-tight text-espresso sm:text-[1.6rem] lg:text-[2.05rem]"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25, ease }}
              >
                {HERO.titleLines[0]} <em className="italic text-clay">{HERO.titleLines[1]}</em>
              </motion.span>
            </span>
          </h1>

          {/* Signature lash-arc draw */}
          <svg
            className="mt-3 h-4 w-56 text-gold sm:w-72"
            viewBox="0 0 280 16"
            fill="none"
            aria-hidden="true"
          >
            <motion.path
              d="M3 13 C60 3, 220 3, 277 9"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.2, delay: 0.7, ease }}
            />
            <motion.path
              d="M60 13.5 l-1.5 -3.5 M140 11.5 l0 -3.6 M218 12.5 l1.5 -3.4"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.6, delay: 1.7, ease }}
            />
          </svg>

          <a
            data-testid="hero-reviews-badge"
            href="https://share.google/alK7xYjbe4TUkQ9eC"
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-5 inline-flex items-center gap-2 rounded-full border border-espresso/15 bg-card/70 px-4 py-2 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold hover:bg-cream"
          >
            <span className="flex gap-0.5" aria-label="5 out of 5 stars">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={13} className="text-gold" fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            <span className="text-[13px] font-semibold text-espresso">5.0</span>
            <span className="text-[13px] text-cocoa">· 110 Google reviews</span>
          </a>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease }}
            className="mt-6 max-w-xl text-base leading-relaxed text-cocoa sm:text-lg"
          >
            {HERO.sub}
          </motion.p>
        </div>

        {/* Image */}
        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.3, ease }}
          className="relative"
        >
          <div className="absolute -inset-3 rounded-[2.4rem] border border-gold/40" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[2rem] shadow-[0_30px_80px_-30px_rgba(44,34,30,0.45)]">
            <img
              src={HERO_IMAGE}
              alt="Close-up of a woman's eye with permanent eyeliner makeup in warm editorial tones"
              className="aspect-[4/3] w-full object-cover sm:aspect-[3/2]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/25 via-transparent to-transparent" />
            <img
              data-testid="hero-signature"
              src="/signature-white.png"
              alt="Sherry Lyn Corvino signature"
              className="pointer-events-none absolute bottom-5 left-5 w-40 opacity-95 sm:w-48"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
